import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { Toaster } from './components/ui/sonner';
import { queryClient } from './lib/queryClient';
import { resolveRedirect } from './lib/redirect';

// Layout Components
import Navbar from './components/Navbar';

// Pages
import LoginPage from './pages/LoginPage';
import LogoutPage from './pages/LogoutPage';
import RegisterPage from './pages/RegisterPage';
import VenuesPage from './pages/VenuesPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import BookingPage from './pages/BookingPage';
import SpacePreviewPage from './pages/SpacePreviewPage';
import VirtualOfficePreviewPage from './pages/VirtualOfficePreviewPage';
import VirtualOfficeIndexPage from './pages/VirtualOfficeIndexPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import ClientDashboard from './pages/ClientDashboard';
import AdminDashboard from './pages/AdminDashboard';
import CenterManagerDashboard from './pages/CenterManagerDashboard';
import SalesDashboard from './pages/SalesDashboard';
import MarketingOpsDashboard from './pages/MarketingOpsDashboard';
import BDTransactionsDashboard from './pages/BDTransactionsDashboard';
import FoundersDashboard from './pages/FoundersDashboard';
import FinanceDashboard from './pages/FinanceDashboard';
import ExplorePage from './pages/ExplorePage';
import FeedbackPage from './pages/FeedbackPage';
import SiteVisitFeedbackPage from './pages/SiteVisitFeedbackPage';
import MemberFeedbackPage from './pages/MemberFeedbackPage';
import GiftPassPage from './pages/GiftPassPage';
import ManagerDashboard from './pages/ManagerDashboard';
import LandlordDashboard from './pages/LandlordDashboard';
import FinanceApprovalsPage from './pages/FinanceApprovalsPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import EcosystemDirectoryPage from './pages/EcosystemDirectoryPage';
import EcosystemProfilePage from './pages/EcosystemProfilePage';
import MyEcosystemProfilePage from './pages/MyEcosystemProfilePage';

import './App.css';

// Proposal Studio carries its own fonts, slides and export libraries, so it loads only when opened.
const ProposalStudioApp = lazy(() => import('./components/proposal_studio/ProposalStudioApp'));
const ProposalApprovePage = lazy(() => import('./components/proposal_studio/ApprovePage'));
const PROPOSAL_STUDIO_ROLES = ['admin', 'hub_manager', 'center_manager', 'cluster_manager', 'sales', 'founders',
  'finance_audit', 'manager', 'finance', 'marketing_ops', 'bd_transactions'];
const StudioFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Roles with their own dedicated dashboard — must never land on the generic
// Client routes (/dashboard, /bookings, /invoices, /credits), whether they
// get there via a stale login redirect, a bookmark, or a direct URL.
// Keep this in sync with the role checks in `AuthRedirect` below.
const CLIENT_ROUTE_ROLE_OVERRIDES = {
  admin: '/admin',
  hub_manager: '/admin',
  center_manager: '/manager',
  cluster_manager: '/manager',
  sales: '/sales',
  founders: '/founders',
  finance_audit: '/finance',
  manager: '/approvals',
  finance: '/finance-approvals',
  landlord: '/landlord',
  marketing_ops: '/marketing-ops',
  bd_transactions: '/bd-transactions',
};

// Roles confined to a single dashboard. They're sent back to it from every
// protected route that doesn't explicitly list them in `allowedRoles` —
// including routes with no role restriction at all (/venues, /booking, ...).
const SINGLE_MODULE_ROLE_HOME = {
  bd_transactions: '/bd-transactions',
};

// Protected Route Component
// `redirectRoleOverrides` sends specific roles to their own dashboard instead
// of rendering this route at all — used on the generic Client routes
// (/dashboard, /bookings, /invoices, /credits) so a role with its own
// dedicated dashboard (e.g. landlord) can never land on the client view,
// even via a direct URL/bookmark.
const ProtectedRoute = ({ children, allowedRoles, redirectRoleOverrides }) => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(returnTo)}`} replace />;
  }

  const lockedHome = SINGLE_MODULE_ROLE_HOME[user?.role];
  if (lockedHome && !allowedRoles?.includes(user.role)) {
    return <Navigate to={lockedHome} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  const override = redirectRoleOverrides && redirectRoleOverrides[user?.role];
  if (override) {
    return <Navigate to={override} replace />;
  }

  return children;
};

// Layout wrapper for dashboard pages
const DashboardLayout = ({ children }) => {
  return (
    <>
      <Navbar />
      <main>{children}</main>
    </>
  );
};

// Hard-navigates to another origin (e.g. back to the marketing website after
// login). Done in an effect so render stays pure.
// The website only sees the shared cw_session cookie, which expires long
// before this app's bearer token — so refresh the cookie first, or the
// website still shows "Log in" and its login link bounces straight back here.
const ExternalRedirect = ({ url }) => {
  const { api } = useAuth();
  useEffect(() => {
    let done = false;
    const go = () => {
      if (!done) {
        done = true;
        window.location.replace(url);
      }
    };
    // Never strand the user here if the call hangs.
    const timer = setTimeout(go, 4000);
    api.post('/auth/session').catch(() => {}).finally(() => {
      clearTimeout(timer);
      go();
    });
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
};

// Helper component to redirect authenticated users based on role
const AuthRedirect = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [searchParams] = useSearchParams();
  
  if (isAuthenticated && user) {
    // Check for redirect param first (from preview page "Book Now" flow)
    const redirectUrl = searchParams.get('redirect');
    const target = resolveRedirect(redirectUrl);
    if (target?.external) {
      return <ExternalRedirect url={target.url} />;
    }
    if (target) {
      return <Navigate to={target.path} replace />;
    }
    // Redirect based on user role
    if (user.role === 'admin' || user.role === 'hub_manager') {
      return <Navigate to="/admin" replace />;
    } else if (user.role === 'center_manager' || user.role === 'cluster_manager') {
      return <Navigate to="/manager" replace />;
    } else if (user.role === 'sales') {
      return <Navigate to="/sales" replace />;
    } else if (user.role === 'founders') {
      return <Navigate to="/founders" replace />;
    } else if (user.role === 'finance_audit') {
      return <Navigate to="/finance" replace />;
    } else if (user.role === 'manager') {
      return <Navigate to="/approvals" replace />;
    } else if (user.role === 'finance') {
      return <Navigate to="/finance-approvals" replace />;
    } else if (user.role === 'landlord') {
      return <Navigate to="/landlord" replace />;
    } else if (user.role === 'marketing_ops') {
      return <Navigate to="/marketing-ops" replace />;
    } else if (user.role === 'bd_transactions') {
      return <Navigate to="/bd-transactions" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }
  
  return children;
};

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      {/* Auth Routes - No navbar */}
      <Route path="/login" element={<AuthRedirect><LoginPage /></AuthRedirect>} />
      <Route path="/register" element={<AuthRedirect><RegisterPage /></AuthRedirect>} />
      {/* Cross-app single logout: the website can't clear this origin's
          localStorage, so it sends the browser here instead. Deliberately not
          wrapped in AuthRedirect — logging out while already logged out must
          still clear state and bounce back, not redirect to a dashboard. */}
      <Route path="/logout" element={<LogoutPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Public Preview Route - No auth required */}
      <Route path="/preview/space/:id" element={<SpacePreviewPage />} />
      <Route path="/virtual-office" element={<VirtualOfficeIndexPage />} />
      <Route path="/preview/virtual-office/:venueId" element={<VirtualOfficePreviewPage />} />
      <Route path="/feedback/:token" element={<FeedbackPage />} />
      <Route path="/site-visit-feedback" element={<SiteVisitFeedbackPage />} />
      {/* Public, but only linked from the integration platform */}
      <Route path="/member-feedback" element={<MemberFeedbackPage />} />
      {/* Public: Sales reps fill it after a site visit; submissions go to Marketing Ops */}
      <Route path="/gift-pass" element={<GiftPassPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />

      {/* Clayworks Ecosystem — B2B internal network */}
      <Route path="/ecosystem" element={<EcosystemDirectoryPage />} />
      <Route path="/ecosystem/my-profile" element={
        <ProtectedRoute>
          <MyEcosystemProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/ecosystem/c/:slug" element={<EcosystemProfilePage />} />
      
      {/* Public Explore - No auth required */}
      <Route path="/" element={<ExplorePage />} />
      <Route path="/explore" element={<ExplorePage />} />
      
      {/* Protected Routes with Navbar */}
      <Route path="/venues" element={
        <ProtectedRoute>
          <DashboardLayout><VenuesPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/venues/:id" element={
        <ProtectedRoute>
          <DashboardLayout><VenuesPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/booking" element={
        <ProtectedRoute>
          <DashboardLayout><BookingPage /></DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Protected Client Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute redirectRoleOverrides={CLIENT_ROUTE_ROLE_OVERRIDES}>
            <DashboardLayout><ClientDashboard /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bookings"
        element={
          <ProtectedRoute redirectRoleOverrides={CLIENT_ROUTE_ROLE_OVERRIDES}>
            <DashboardLayout><ClientDashboard /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <ProtectedRoute redirectRoleOverrides={CLIENT_ROUTE_ROLE_OVERRIDES}>
            <DashboardLayout><ClientDashboard /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/credits"
        element={
          <ProtectedRoute redirectRoleOverrides={CLIENT_ROUTE_ROLE_OVERRIDES}>
            <DashboardLayout><ClientDashboard /></DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'hub_manager']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/*" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'hub_manager']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />

      {/* Center Manager Routes */}
      <Route 
        path="/manager" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'hub_manager', 'center_manager', 'cluster_manager']}>
            <CenterManagerDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/manager/*" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'hub_manager', 'center_manager', 'cluster_manager']}>
            <CenterManagerDashboard />
          </ProtectedRoute>
        } 
      />

      {/* Founders Dashboard Routes */}
      <Route 
        path="/founders" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'hub_manager', 'founders']}>
            <FoundersDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/founders/*" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'hub_manager', 'founders']}>
            <FoundersDashboard />
          </ProtectedRoute>
        } 
      />

      {/* Sales Dashboard Routes */}
      <Route 
        path="/sales" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'hub_manager', 'sales']}>
            <SalesDashboard />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/sales/*"
        element={
          <ProtectedRoute allowedRoles={['admin', 'hub_manager', 'sales']}>
            <SalesDashboard />
          </ProtectedRoute>
        }
      />

      {/* Marketing Ops Dashboard Routes — exclusive owner of the stock
          inventory system; Sales Persons have no access here or anywhere
          inventory-related. */}
      <Route
        path="/marketing-ops"
        element={
          <ProtectedRoute allowedRoles={['admin', 'hub_manager', 'marketing_ops']}>
            <MarketingOpsDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketing-ops/*"
        element={
          <ProtectedRoute allowedRoles={['admin', 'hub_manager', 'marketing_ops']}>
            <MarketingOpsDashboard />
          </ProtectedRoute>
        }
      />

      {/* BD Transactions Dashboard — read-only Inventory, nothing else */}
      <Route
        path="/bd-transactions"
        element={
          <ProtectedRoute allowedRoles={['bd_transactions']}>
            <BDTransactionsDashboard />
          </ProtectedRoute>
        }
      />

      {/* Proposal Studio — access is by studio membership, checked by the backend */}
      <Route
        path="/proposal-studio/approve/:token"
        element={<Suspense fallback={<StudioFallback />}><ProposalApprovePage /></Suspense>}
      />
      <Route
        path="/proposal-studio/*"
        element={
          <ProtectedRoute allowedRoles={PROPOSAL_STUDIO_ROLES}>
            <Suspense fallback={<StudioFallback />}><ProposalStudioApp /></Suspense>
          </ProtectedRoute>
        }
      />

      {/* Payment Success Page — verifies payment and confirms booking */}
      <Route path="/payment-success" element={<PaymentSuccessPage />} />

      {/* Finance Dashboard Routes */}
      <Route 
        path="/finance" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'hub_manager', 'finance_audit']}>
            <FinanceDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/finance/*" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'hub_manager', 'finance_audit']}>
            <FinanceDashboard />
          </ProtectedRoute>
        } 
      />

      {/* Approvals routes (Manager + Finance roles) */}
      <Route
        path="/approvals"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin', 'hub_manager']}>
            <ManagerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/finance-approvals"
        element={
          <ProtectedRoute allowedRoles={['finance', 'admin', 'hub_manager']}>
            <FinanceApprovalsPage />
          </ProtectedRoute>
        }
      />

      {/* Landlord Dashboard Routes */}
      <Route
        path="/landlord"
        element={
          <ProtectedRoute allowedRoles={['admin', 'hub_manager', 'landlord']}>
            <LandlordDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/landlord/*"
        element={
          <ProtectedRoute allowedRoles={['admin', 'hub_manager', 'landlord']}>
            <LandlordDashboard />
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to login */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-background transition-colors duration-300">
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <BrowserRouter>
            <AuthProvider>
              <AppRoutes />
              <Toaster position="top-right" richColors />
            </AuthProvider>
          </BrowserRouter>
        </ThemeProvider>
      </QueryClientProvider>
    </div>
  );
}

export default App;
